import React, { useEffect, useState } from "react";
import { Alert, Box, Button, Card, CardContent, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Snackbar, TextField, Tooltip, Typography } from "@mui/material";
import { ACCENT, TEXT } from "../../dark-theme/palette";
import { usePagesTranslation } from "../locales";
import S3Service from "../../services/S3Service";
import SubscriptionService from "../../services/SubscriptionService";
import UserProfileService from "../profile/UserProfileService";
import TutorPdfService from "../../services/TutorPdfService";
import { isTutorStandardUser } from "../../services/PayPlanUtil";
import {
    DEFAULT_TUTOR_PLAN,
    TUTOR_EXTRA_PDF_PLAN,
    PlanCardOfferExtras,
    PlanPickerFootnotes,
    SUBSCRIPTION_PLANS,
    getSubscriptionPlanPickerEntries,
} from "../subscription/PanelPlan";
import TutorsWorkspaceShell from "./TutorsWorkspaceShell";
import useTutorHub from "./useTutorHub";
import { TUTOR_DETAILS_KEYS } from "./tutorHubDetails";

const TUTOR_PLAN_FEATURES = [
    "tutors.plan-feature-exports",
    "tutors.plan-feature-library",
    "tutors.plan-feature-clean",
    "tutors.plan-feature-license",
];

const EXTRA_PDF_FEATURES = [
    ["tutors.extra-pdf-feature-instant", "Adds +50 PDF downloads instantly"],
    ["tutors.extra-pdf-feature-expires", "Never expires while subscription is active"],
    ["tutors.extra-pdf-feature-order", "Only consumed after monthly base quota is used"],
];

export default function TutorsSettingsPage() {
    const { t } = usePagesTranslation();
    const { cloudUser, details, loading, saving, error, setError, patchDetails, reload } = useTutorHub();
    const [orgName, setOrgName] = useState("");
    const [logoUrl, setLogoUrl] = useState("");
    const [uploading, setUploading] = useState(false);
    const [snack, setSnack] = useState("");
    const [selectedPlan, setSelectedPlan] = useState(DEFAULT_TUTOR_PLAN);
    const [isProcessing, setIsProcessing] = useState(false);
    const [checkoutError, setCheckoutError] = useState("");
    const [quota, setQuota] = useState(null);
    const [cancelOpen, setCancelOpen] = useState(false);
    const [cancelling, setCancelling] = useState(false);
    const entitled = isTutorStandardUser(cloudUser);
    const tutorPlanEntries = getSubscriptionPlanPickerEntries({ catalog: "tutors" });
    const tutorPlanKeys = tutorPlanEntries.map(([key]) => key);

    useEffect(() => {
        const stored = String(details?.[TUTOR_DETAILS_KEYS.organizationName] || "");
        const org = stored.trim().toLowerCase() === "my persional org" ? "My Personal Org" : stored;
        setOrgName(org);
        setLogoUrl(String(details?.[TUTOR_DETAILS_KEYS.logoUrl] || ""));
    }, [details]);

    useEffect(() => {
        if (loading) {
            return undefined;
        }
        let cancelled = false;
        TutorPdfService.fetchQuota()
            .then((next) => {
                if (!cancelled) {
                    setQuota(next);
                }
            })
            .catch(() => {});
        return () => {
            cancelled = true;
        };
    }, [loading]);

    const handleSave = async (event) => {
        event.preventDefault();
        await patchDetails({
            [TUTOR_DETAILS_KEYS.organizationName]: orgName.trim() || null,
            [TUTOR_DETAILS_KEYS.logoUrl]: logoUrl.trim() || null,
        });
        setSnack(t("tutors.settings-saved", "Settings saved."));
    };

    const handleLogo = async (event) => {
        const file = event.target.files?.[0];
        event.target.value = "";
        if (!file) {
            return;
        }
        setUploading(true);
        setError("");
        try {
            const url = await S3Service.uploadTutorLogo(file);
            setLogoUrl(url || "");
            await patchDetails({
                [TUTOR_DETAILS_KEYS.organizationName]: orgName.trim() || null,
                [TUTOR_DETAILS_KEYS.logoUrl]: url || null,
            });
            setSnack(t("tutors.settings-logo-saved", "Logo uploaded."));
        } catch (err) {
            setError(err?.message || t("tutors.settings-logo-error", "Could not upload logo."));
        } finally {
            setUploading(false);
        }
    };

    const handleTutorSubscribe = async () => {
        setCheckoutError("");
        setIsProcessing(true);
        try {
            const result = await SubscriptionService.handleSubscribe(selectedPlan, SUBSCRIPTION_PLANS);
            if (result.requiresLogin) {
                setCheckoutError(t("subs.dialog.login-required-desc"));
                return;
            }
            if (!result.success) {
                setCheckoutError(result.error || t("profile.userProfile.snackbar-upgrade-error"));
            }
        } finally {
            setIsProcessing(false);
        }
    };

    const handleCancelSubscription = async () => {
        setCancelling(true);
        setCheckoutError("");
        try {
            await UserProfileService.cancelTutorSubscription();
            await reload();
            setCancelOpen(false);
            setSnack(t("tutors.settings-cancel-done", "Subscription canceled."));
        } catch (err) {
            setCheckoutError(err?.message || t("tutors.settings-cancel-error", "Could not cancel subscription."));
            setCancelOpen(false);
        } finally {
            setCancelling(false);
        }
    };

    const handleBuyExtra = async () => {
        setCheckoutError("");
        setIsProcessing(true);
        try {
            const result = await SubscriptionService.handleSubscribe(TUTOR_EXTRA_PDF_PLAN, SUBSCRIPTION_PLANS);
            if (result.requiresLogin) {
                setCheckoutError(t("subs.dialog.login-required-desc"));
                return;
            }
            if (!result.success) {
                setCheckoutError(result.error || t("profile.userProfile.snackbar-upgrade-error"));
            }
        } finally {
            setIsProcessing(false);
        }
    };

    const extraPlan = SUBSCRIPTION_PLANS[TUTOR_EXTRA_PDF_PLAN];

    return (
        <TutorsWorkspaceShell
            title={t("tutors.settings-title", "Settings")}
            loading={loading}
            error={error}
        >
            <Typography variant="body1" sx={{ color: TEXT.secondary, lineHeight: 1.7, mb: 3 }}>
                {t(
                    "tutors.settings-body",
                    "Organization name and custom logo appear on worksheet PDFs. Subscription status is from Tutor Standard on Stripe.",
                )}
            </Typography>

            <Box component="form" onSubmit={handleSave} sx={{ mb: 4 }}>
                <TextField
                    label={t("tutors.settings-org", "Organization name")}
                    placeholder={t("tutors.settings-org-default", "My Personal Org")}
                    value={orgName}
                    onChange={(event) => setOrgName(event.target.value)}
                    fullWidth
                    sx={{ mb: 2 }}
                    slotProps={{ htmlInput: { maxLength: 120 } }}
                />
                <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2, flexWrap: "wrap" }}>
                    {logoUrl ? (
                        <Box
                            component="img"
                            src={logoUrl}
                            alt=""
                            sx={{ width: 72, height: 72, objectFit: "contain", borderRadius: 1 }}
                        />
                    ) : null}
                    <Button
                        component="label"
                        variant="outlined"
                        disabled={uploading || saving}
                        sx={{ minHeight: 44, textTransform: "none" }}
                    >
                        {uploading
                            ? t("btn.loading", "Loading...")
                            : t("tutors.settings-logo-upload", "Upload logo")}
                        <input type="file" accept="image/*" hidden onChange={handleLogo} />
                    </Button>
                </Box>
                <Button
                    type="submit"
                    variant="contained"
                    disabled={saving || uploading}
                    sx={{ minHeight: 44, textTransform: "none", fontWeight: 700, bgcolor: ACCENT.main }}
                >
                    {t("btn.save", "Save")}
                </Button>
            </Box>

            <Card sx={{ p: 2.5 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                    {t("tutors.settings-sub-title", "Subscription")}
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 1.5 }}>
                    <Chip
                        label={entitled
                            ? t("tutors.settings-sub-active", "Tutor Standard — active")
                            : t("tutors.settings-sub-inactive", "Tutor Standard — not active")}
                        color={entitled ? "success" : "default"}
                    />
                    <Chip
                        variant="outlined"
                        label={t("tutors.extra-pdf-credits-badge", {
                            left: quota?.monthlyLeft ?? quota?.monthlyLimit ?? 50,
                            limit: quota?.monthlyLimit ?? 50,
                            extra: quota?.extraPdfCredits ?? 0,
                            defaultValue: "Credits: {{left}}/{{limit}} Base | +{{extra}} Extra",
                        })}
                    />
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {t(
                        "tutors.settings-sub-note",
                        "Canceling or letting your subscription expire on Stripe will lock hub access. Your stored puzzles remain safe.",
                    )}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {t(
                        "tutors.plan-body",
                        "Create custom puzzle lessons and printable worksheets for individual tutors, homeschool parents, and private ESL instructors.",
                    )}
                </Typography>
                {checkoutError ? (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {checkoutError}
                    </Alert>
                ) : null}
                <Box sx={{ display: "flex", gap: 2, flexWrap: { xs: "wrap", sm: "nowrap" }, mb: 2, alignItems: "stretch" }}>
                    {tutorPlanEntries.map(([key, plan]) => (
                        <Card
                            key={key}
                            sx={{
                                flex: { xs: "1 1 100%", sm: "1 1 0" },
                                minWidth: { xs: "100%", sm: 0 },
                                maxWidth: { xs: "100%", sm: 360 },
                                cursor: entitled ? "default" : "pointer",
                                border: selectedPlan === key ? "2px solid #3BA666" : "1px solid #e0e0e0",
                                position: "relative",
                                overflow: "visible",
                                "&:hover": entitled ? undefined : { borderColor: "#3BA666" },
                            }}
                            onClick={() => {
                                if (!entitled) {
                                    setSelectedPlan(key);
                                }
                            }}
                        >
                            {plan.popular ? (
                                <Chip
                                    label={t("subs.dialog.most-popular")}
                                    size="small"
                                    sx={{
                                        position: "absolute",
                                        top: -12,
                                        left: "50%",
                                        transform: "translateX(-50%)",
                                        backgroundColor: "#3BA666",
                                        color: "white",
                                        fontSize: "0.7rem",
                                        zIndex: 1,
                                    }}
                                />
                            ) : null}
                            <CardContent sx={{ textAlign: "center", pt: plan.popular ? 3 : 2 }}>
                                <Typography variant="h6" gutterBottom>
                                    {t("tutors.plan-badge", "Tutor Standard")}
                                </Typography>
                                <Typography variant="h4" color="primary" sx={{ fontWeight: "bold" }}>
                                    {plan.price}
                                </Typography>
                                <PlanCardOfferExtras planKey={key} plan={plan} hideBestValue />
                                <Box component="ul" sx={{ m: 0, mt: 1.5, pl: 2.25, textAlign: "left" }}>
                                    {TUTOR_PLAN_FEATURES.map((featureKey) => (
                                        <Typography
                                            key={featureKey}
                                            component="li"
                                            variant="body2"
                                            color="text.secondary"
                                            sx={{ mb: 0.5, lineHeight: 1.4 }}
                                        >
                                            {t(featureKey, {
                                                "tutors.plan-feature-exports": "50 high-resolution PDF exports / month",
                                                "tutors.plan-feature-library": "Save up to 100 custom puzzles in library",
                                                "tutors.plan-feature-clean": "Clean printable downloads (no watermark)",
                                                "tutors.plan-feature-license": "Commercial teaching license",
                                            }[featureKey])}
                                        </Typography>
                                    ))}
                                </Box>
                            </CardContent>
                        </Card>
                    ))}
                    <Card
                        sx={{
                            flex: { xs: "1 1 100%", sm: "1 1 0" },
                            minWidth: { xs: "100%", sm: 0 },
                            maxWidth: { xs: "100%", sm: 360 },
                            border: "1px solid #e0e0e0",
                        }}
                    >
                        <CardContent sx={{ textAlign: "center" }}>
                            <Typography variant="h6" gutterBottom>
                                {t("tutors.extra-pdf-title", "Extra 50 PDF Exports")}
                            </Typography>
                            <Typography variant="h4" color="primary" sx={{ fontWeight: "bold" }}>
                                {extraPlan.price}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                {t("tutors.extra-pdf-price-note", "One-time payment")}
                            </Typography>
                            <Box component="ul" sx={{ m: 0, mt: 1.5, pl: 2.25, textAlign: "left" }}>
                                {EXTRA_PDF_FEATURES.map(([featureKey, fallback]) => (
                                    <Typography
                                        key={featureKey}
                                        component="li"
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{ mb: 0.5, lineHeight: 1.4 }}
                                    >
                                        {t(featureKey, fallback)}
                                    </Typography>
                                ))}
                            </Box>
                            {entitled ? null : (
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
                                    {t("tutors.extra-pdf-need-plan", "Available with an active Tutor Standard subscription.")}
                                </Typography>
                            )}
                            <Tooltip
                                title={entitled
                                    ? ""
                                    : t(
                                        "tutors.extra-pdf-buy-disabled",
                                        "Requires an active Tutor Standard subscription",
                                    )}
                                disableHoverListener={entitled}
                            >
                                <span style={{ display: "inline-flex" }}>
                                    <Button
                                        variant="contained"
                                        disabled={isProcessing || !entitled}
                                        onClick={handleBuyExtra}
                                        sx={{
                                            minHeight: 44,
                                            mt: 2,
                                            textTransform: "none",
                                            fontWeight: 700,
                                            bgcolor: ACCENT.main,
                                            "&:hover": { bgcolor: ACCENT.dark },
                                        }}
                                    >
                                        {isProcessing ? t("subs.dialog.processing") : t("tutors.extra-pdf-buy", "Buy 50 Extra Exports")}
                                    </Button>
                                </span>
                            </Tooltip>
                        </CardContent>
                    </Card>
                </Box>
                <PlanPickerFootnotes planKeys={tutorPlanKeys} catalog="tutors" />
                {entitled ? (
                    <Button
                        variant="outlined"
                        color="error"
                        disabled={cancelling}
                        onClick={() => setCancelOpen(true)}
                        sx={{ minHeight: 44, mt: 2, textTransform: "none", fontWeight: 700 }}
                    >
                        {t("tutors.settings-cancel", "Cancel subscription")}
                    </Button>
                ) : (
                    <Button
                        variant="contained"
                        disabled={isProcessing}
                        onClick={handleTutorSubscribe}
                        sx={{
                            minHeight: 44,
                            mt: 2,
                            textTransform: "none",
                            fontWeight: 700,
                            bgcolor: ACCENT.main,
                            "&:hover": { bgcolor: ACCENT.dark },
                        }}
                    >
                        {isProcessing ? t("subs.dialog.processing") : t("subs.dialog.upgrade-now")}
                    </Button>
                )}
            </Card>
            <Dialog
                open={cancelOpen}
                onClose={() => {
                    if (!cancelling) {
                        setCancelOpen(false);
                    }
                }}
                fullWidth
                maxWidth="xs"
            >
                <DialogTitle>{t("tutors.settings-cancel", "Cancel subscription")}</DialogTitle>
                <DialogContent>
                    <Typography sx={{ lineHeight: 1.7 }}>
                        {t(
                            "tutors.settings-cancel-body",
                            "This ends Tutor Standard. Your saved puzzles stay in the library.",
                        )}
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button
                        onClick={() => setCancelOpen(false)}
                        disabled={cancelling}
                        sx={{ minHeight: 44, textTransform: "none" }}
                    >
                        {t("btn.cancel", "Cancel")}
                    </Button>
                    <Button
                        variant="contained"
                        color="error"
                        onClick={handleCancelSubscription}
                        disabled={cancelling}
                        sx={{ minHeight: 44, textTransform: "none" }}
                    >
                        {cancelling
                            ? t("btn.loading", "Loading...")
                            : t("tutors.settings-cancel", "Cancel subscription")}
                    </Button>
                </DialogActions>
            </Dialog>
            <Snackbar
                open={Boolean(snack)}
                autoHideDuration={3000}
                onClose={() => setSnack("")}
                message={snack}
            />
        </TutorsWorkspaceShell>
    );
}
